(() => {
  const ROOT_SELECTOR = '[data-tonys-counter]';
  const JOURNEY_KEY = 'tonys-counter-journey-id';
  const HANDOFF_EXPLAINED_KEY = 'tonys-counter-handoff-explained-v1';
  const FALLBACK_CATEGORY_TERMS = {
    poultry: ['chicken', 'turkey'],
    'potatoes-sides': ['potato', 'stuffing', 'dressing', 'side'],
    'soups-stuffing': ['soup', 'stew', 'stuffing', 'dressing'],
  };

  class TonysCounter {
    constructor(root) {
      this.root = root;
      this.endpoint = (root.dataset.endpoint || '').trim();
      this.productFormId = root.dataset.productFormId;
      this.context = this.readContext();
      this.form = root.querySelector('[data-tonys-counter-form]');
      this.ingredientsInput = root.querySelector('[data-tonys-counter-ingredients]');
      this.submitButton = root.querySelector('[data-tonys-counter-submit]');
      this.status = root.querySelector('[data-tonys-counter-status]');
      this.results = root.querySelector('[data-tonys-counter-results]');
      this.abortController = null;
      this.journeyId = this.getJourneyId();

      if (!this.context || !this.form || !this.results) return;

      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
      this.form.addEventListener('change', (event) => this.handleChange(event));
      this.ingredientsInput?.addEventListener('input', () => this.handleIngredientsInput());
      this.ingredientsInput?.addEventListener('blur', () => {
        if (this.ingredientsInput.value.trim()) {
          this.track('tonys_counter_ingredient_entered');
        }
      });
      this.results.addEventListener('click', (event) => this.handleResultClick(event));

      this.track('tonys_counter_viewed');
    }

    readContext() {
      const contextNode = this.root.querySelector('[data-tonys-counter-context]');
      if (!contextNode) return null;

      try {
        return JSON.parse(contextNode.textContent);
      } catch (error) {
        console.warn("Tony's Counter could not read product context.", error);
        return null;
      }
    }

    getJourneyId() {
      try {
        const existing = window.sessionStorage.getItem(JOURNEY_KEY);
        if (existing) return existing;

        const id = window.crypto?.randomUUID?.() || `tc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        window.sessionStorage.setItem(JOURNEY_KEY, id);
        return id;
      } catch (_error) {
        return `tc-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      }
    }

    handleChange(event) {
      if (event.target.matches('input[type="radio"]')) {
        this.ingredientsInput?.setCustomValidity('');
        this.track('tonys_counter_category_selected', { category: event.target.value });

        if (event.target.value === 'something_else') {
          window.requestAnimationFrame(() => this.ingredientsInput?.focus());
        }
      }
    }

    handleIngredientsInput() {
      this.ingredientsInput?.setCustomValidity('');

      if (!this.ingredientsInput?.value.trim()) return;

      const somethingElseInput = this.form.querySelector('input[type="radio"][value="something_else"]');
      if (somethingElseInput) {
        somethingElseInput.checked = true;
        this.status.textContent = 'Something Else selected. Tony will use what you typed.';
        return;
      }

      const selectedInput = this.form.querySelector('input[type="radio"]:checked');
      if (selectedInput) selectedInput.checked = false;
      this.status.textContent = 'Tony will use what you typed as the cooking choice.';
    }

    async handleSubmit(event) {
      event.preventDefault();

      const categoryInput = this.form.querySelector('input[type="radio"]:checked');
      const ingredients = this.readIngredients();
      const category = categoryInput?.value || (ingredients.length ? 'something_else' : null);

      if (!category || (category === 'something_else' && ingredients.length === 0)) {
        this.ingredientsInput?.setCustomValidity('Choose an option or tell Tony what you are cooking.');
        this.ingredientsInput?.reportValidity();
        this.status.textContent = 'Choose a cooking option or tell Tony what you have.';
        return;
      }

      this.ingredientsInput?.setCustomValidity('');
      const payload = {
        contextId: this.context.contextId || null,
        shopifyProductId: this.context.shopifyProductId,
        productHandle: this.context.productHandle,
        actProductSlug: this.context.actProductSlug,
        category,
        choiceKey: category,
        choiceGenerationId: this.context.choiceGenerationId || null,
        ingredients,
        journeyId: this.journeyId,
      };
      const collection = this.buildCollectionContext(category);
      if (collection) payload.collection = collection;

      this.setLoading(true, category);
      this.track('tonys_counter_request_started', {
        category,
        choiceGenerationId: payload.choiceGenerationId,
      });

      try {
        let data;
        let usedApprovedRecipeFallback = false;

        if (this.endpoint) {
          data = await this.requestSuggestions(payload);
        } else {
          data = this.getApprovedRecipeFallback(category);
          usedApprovedRecipeFallback = true;
        }

        if (!Array.isArray(data.suggestions) || data.suggestions.length === 0) {
          const fallback = this.getApprovedRecipeFallback(category);
          if (fallback.suggestions.length === 0) throw new Error('No suggestions returned');
          data = fallback;
          usedApprovedRecipeFallback = true;
        }

        this.renderSuggestions(data, payload, usedApprovedRecipeFallback);
        this.track('tonys_counter_suggestions_returned', {
          category,
          count: data.suggestions.length,
          fallback: usedApprovedRecipeFallback,
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.renderError(payload);
        this.track('tonys_counter_worker_error', { category, message: error.message });
      } finally {
        this.setLoading(false);
      }
    }

    buildCollectionContext(category) {
      const configuration = this.context.choiceConfiguration;
      if (this.context.counterMode !== 'collection' || configuration?.schemaVersion !== 2) return null;

      const components = Array.isArray(configuration.components)
        ? configuration.components.map((component) => ({
            actProductSlug: component.actProductSlug,
            generationId: component.generationId,
            label: component.label,
          }))
        : [];
      const utilityChoice = category === 'surprise' || category === 'something_else';
      const choice = utilityChoice
        ? null
        : configuration.choices?.find((candidate) => candidate.key === category);
      const routes = Array.isArray(choice?.routes)
        ? choice.routes.slice(0, 3)
        : choice?.route
          ? [choice.route]
          : [];
      const route = routes[0];

      return {
        components,
        selected: route
          ? {
              actProductSlug: route.actProductSlug,
              generationId: route.generationId,
              choiceKey: route.choiceKey,
            }
          : null,
        alternates: utilityChoice
          ? []
          : routes.slice(1).map((alternate) => ({
              actProductSlug: alternate.actProductSlug,
              generationId: alternate.generationId,
              choiceKey: alternate.choiceKey,
            })),
      };
    }

    readIngredients() {
      return (this.ingredientsInput?.value || '')
        .split(/[,;\n]/)
        .map((ingredient) => ingredient.trim())
        .filter(Boolean)
        .slice(0, 3);
    }

    async requestSuggestions(payload) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const timeout = window.setTimeout(() => this.abortController.abort(), 14000);
      const startedAt = window.performance.now();

      try {
        const response = await window.fetch(this.endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Suggestion request failed with ${response.status}`);
        const data = await response.json();
        this.track('tonys_counter_response_latency', {
          milliseconds: Math.round(window.performance.now() - startedAt),
        });
        return data;
      } finally {
        window.clearTimeout(timeout);
      }
    }

    getApprovedRecipeFallback(category) {
      const recipes = Array.isArray(this.context.actRecipes)
        ? this.context.actRecipes.filter((recipe) => recipe?.title && recipe?.url)
        : [];
      const categoryTerms =
        category === 'surprise' || category === 'something_else'
          ? []
          : FALLBACK_CATEGORY_TERMS[category] || [category.toLowerCase()];
      const matchingRecipes = categoryTerms.length
        ? recipes.filter((recipe) => {
            const title = recipe.title.toLowerCase();
            return categoryTerms.some((term) => title.includes(term));
          })
        : recipes;
      const selectedRecipes = (matchingRecipes.length ? matchingRecipes : recipes).slice(0, 3);

      return {
        message: selectedRecipes.length
          ? "Let's put that jar to work. Here are a few tested places to start."
          : 'Tony needs one more detail before he can build your dinner plan.',
        suggestions: selectedRecipes.map((recipe) => ({
          title: recipe.title,
          time: recipe.time,
          method: 'Chef Tony recipe',
          why: `A tested way to put ${this.context.productTitle} to work.`,
          fullRecipeUrl: recipe.url,
        })),
      };
    }

    renderSuggestions(data, payload, fallback) {
      this.results.replaceChildren();
      this.results.hidden = false;

      const heading = this.createElement('h3', 'tonys-counter__result-heading', 'Dinner problem solved.');
      const message = this.createElement(
        'p',
        'tonys-counter__message',
        data.message || `Tony found a few ways to use ${this.context.productTitle}.`,
      );
      const list = this.createElement('ol', 'tonys-counter__suggestions');

      data.suggestions.slice(0, 5).forEach((suggestion, index) => {
        const item = this.createElement('li', 'tonys-counter__suggestion');
        if (suggestion.seasoningLabel) {
          item.append(this.createElement(
            'p',
            'tonys-counter__suggestion-seasoning',
            `Use ${suggestion.seasoningLabel}`,
          ));
        }
        const title = this.createElement('h4', 'tonys-counter__suggestion-title', suggestion.title || 'Chef Tony recipe');
        const metaParts = [suggestion.time, suggestion.serves, suggestion.method].filter(Boolean);

        item.append(title);
        if (metaParts.length) {
          item.append(this.createElement('p', 'tonys-counter__suggestion-meta', metaParts.join(' | ')));
        }
        if (suggestion.why) {
          item.append(this.createElement('p', 'tonys-counter__suggestion-why', suggestion.why));
        }

        const linkText = fallback ? 'Open the full recipe' : 'Create this recipe with Tony';
        const link = this.createElement('a', 'tonys-counter__suggestion-link', linkText);
        link.href = this.buildHandoffUrl(suggestion.fullRecipeUrl, payload, suggestion);
        link.dataset.tonysCounterRecipe = String(index);
        link.dataset.recipeTitle = suggestion.title || '';
        item.append(link);
        list.append(item);
      });

      const actions = this.createElement('div', 'tonys-counter__result-actions');
      if (this.context.productAvailability) {
        const addButton = this.createElement('button', 'button button--primary', 'Add to cart');
        addButton.type = 'button';
        addButton.dataset.tonysCounterAdd = '';
        actions.prepend(addButton);
      }

      this.results.append(heading, message, list, actions);
      this.status.textContent = `${data.suggestions.length} recipe ideas are ready.`;

      if (fallback) {
        this.results.dataset.fallback = 'approved-recipes';
      } else {
        delete this.results.dataset.fallback;
      }
    }

    renderError(payload) {
      this.results.replaceChildren();
      this.results.hidden = false;

      const error = this.createElement('div', 'tonys-counter__error');
      error.append(
        this.createElement('h3', 'tonys-counter__result-heading', 'Tony needs another minute.'),
        this.createElement(
          'p',
          'tonys-counter__message',
          'The Counter did not answer this time, but your product page and cart are still ready.',
        ),
      );

      const link = this.createElement('a', 'tonys-counter__suggestion-link', 'Continue with Ask Chef Tony');
      link.href = this.buildHandoffUrl(this.context.askTonyUrl, payload);
      link.dataset.tonysCounterFullExperience = '';
      error.append(link);
      this.results.append(error);
      this.status.textContent = 'Tony could not load recipe ideas. A link to Ask Chef Tony is available.';
    }

    buildHandoffUrl(rawUrl, payload, suggestion = null) {
      const base = rawUrl || this.context.askTonyUrl || 'https://www.askcheftony.com/make-a-recipe/?workflow=scratch';
      const ideaTitle = typeof suggestion === 'string' ? suggestion : suggestion?.title || '';
      const seasoningSlug = suggestion?.seasoningActProductSlug || this.context.actProductSlug;
      const seasoningLabel = suggestion?.seasoningLabel || this.context.productTitle;

      try {
        const url = new URL(base, window.location.origin);
        if (/^(?:www\.)?askcheftony\.com$/i.test(url.hostname) && url.pathname === '/') {
          url.pathname = '/make-a-recipe/';
        }
        if (!url.searchParams.has('workflow')) url.searchParams.set('workflow', 'scratch');
        url.searchParams.set('sp_product', seasoningSlug);
        url.searchParams.set('sp_product_id', this.context.shopifyProductId);
        url.searchParams.set('sp_product_name', seasoningLabel);
        url.searchParams.set('sp_source', 'tonys-counter');
        url.searchParams.set('sp_category', payload.category);
        url.searchParams.set('sp_journey', this.journeyId);
        if (payload.ingredients.length) url.searchParams.set('sp_ingredients', payload.ingredients.join(','));
        if (ideaTitle) url.searchParams.set('sp_idea', ideaTitle);
        url.searchParams.set('sp_offer_handle', this.context.productHandle);
        url.searchParams.set('sp_offer_product_id', this.context.shopifyProductId);
        url.searchParams.set('sp_offer_name', this.context.productTitle);
        url.searchParams.set('sp_offer_url', this.context.productPageUrl);
        if (this.context.productImage) url.searchParams.set('sp_offer_image', this.context.productImage);
        if (this.context.productPriceCents) {
          url.searchParams.set('sp_offer_price', String(this.context.productPriceCents));
        }
        url.searchParams.set('sp_offer_kind', this.context.counterMode === 'collection' ? 'collection' : 'seasoning');
        return url.toString();
      } catch (_error) {
        return base;
      }
    }

    handleResultClick(event) {
      const recipeLink = event.target.closest('[data-tonys-counter-recipe]');
      if (recipeLink) {
        this.track('tonys_counter_recipe_opened', { recipeTitle: recipeLink.dataset.recipeTitle });
        if (this.shouldExplainHandoff()) {
          event.preventDefault();
          this.openHandoffDialog(recipeLink.href, recipeLink.dataset.recipeTitle);
        }
        return;
      }

      const fullExperienceLink = event.target.closest('[data-tonys-counter-full-experience]');
      if (fullExperienceLink) {
        this.track('tonys_counter_full_experience_opened');
        if (this.shouldExplainHandoff()) {
          event.preventDefault();
          this.openHandoffDialog(fullExperienceLink.href);
        }
        return;
      }

      if (event.target.closest('[data-tonys-counter-add]')) {
        this.addToCart();
      }
    }

    shouldExplainHandoff() {
      if (this.handoffExplained) return false;

      try {
        return window.localStorage.getItem(HANDOFF_EXPLAINED_KEY) !== 'true';
      } catch (_error) {
        return true;
      }
    }

    rememberHandoffExplanation() {
      this.handoffExplained = true;
      try {
        window.localStorage.setItem(HANDOFF_EXPLAINED_KEY, 'true');
      } catch (_error) {
        // The in-memory flag still prevents repeat friction during this visit.
      }
    }

    openHandoffDialog(destination, recipeTitle = '') {
      document.querySelector('[data-tonys-counter-handoff-dialog]')?.remove();

      const dialog = document.createElement('dialog');
      dialog.className = 'tonys-counter__handoff-dialog';
      dialog.dataset.tonysCounterHandoffDialog = '';
      dialog.setAttribute('aria-labelledby', 'tonys-counter-handoff-title');

      const content = this.createElement('div', 'tonys-counter__handoff-content');
      const eyebrow = this.createElement('p', 'tonys-counter__handoff-eyebrow', 'Ask Chef Tony');
      const title = this.createElement(
        'h3',
        'tonys-counter__handoff-title',
        "You're heading to Tony's kitchen.",
      );
      title.id = 'tonys-counter-handoff-title';

      const detail = this.createElement(
        'p',
        'tonys-counter__handoff-detail',
        recipeTitle
          ? `Tony will ask a few quick questions, then create ${recipeTitle} for your kitchen on askcheftony.com.`
          : 'Tony will ask a few quick questions, then create a recipe for your kitchen on askcheftony.com.',
      );
      const reassurance = this.createElement(
        'p',
        'tonys-counter__handoff-reassurance',
        'This product stays with your recipe, so you can return to Salted Perfection to purchase it when you are ready.',
      );
      const account = this.createElement(
        'p',
        'tonys-counter__handoff-account',
        'Already use Ask Chef Tony? You can sign in there to save the recipe.',
      );

      const actions = this.createElement('div', 'tonys-counter__handoff-actions');
      const continueLink = this.createElement('a', 'button button--primary', 'Continue to my recipe');
      continueLink.href = destination;
      continueLink.addEventListener('click', () => {
        this.rememberHandoffExplanation();
        this.track('tonys_counter_handoff_confirmed', { recipeTitle });
      });

      const cancelButton = this.createElement('button', 'button button--secondary', 'Stay here');
      cancelButton.type = 'button';
      cancelButton.addEventListener('click', () => dialog.close());
      dialog.addEventListener('close', () => dialog.remove());

      actions.append(continueLink, cancelButton);
      content.append(eyebrow, title, detail, reassurance, account, actions);
      dialog.append(content);
      document.body.append(dialog);

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        this.rememberHandoffExplanation();
        window.location.assign(destination);
      }

      this.track('tonys_counter_handoff_explained', { recipeTitle });
    }

    addToCart() {
      const productForm = document.getElementById(this.productFormId);
      const addButton = productForm?.querySelector('[name="add"]');

      if (addButton && !addButton.disabled) {
        this.track('tonys_counter_add_to_cart_clicked', {
          variantId: productForm.querySelector('[name="id"]')?.value || this.context.selectedVariantId,
        });
        addButton.click();
      }
    }

    setLoading(isLoading, category = '') {
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', String(isLoading));
      if (!isLoading) return;

      const isDynamicChoice = category === 'surprise' || category === 'something_else';
      this.status.textContent = isDynamicChoice
        ? 'Tony is creating something just for you. This can take about 10 seconds.'
        : "Loading Tony's ready-to-go meal ideas.";
    }

    track(name, detail = {}) {
      const payload = {
        event: name,
        journeyId: this.journeyId,
        productId: this.context?.shopifyProductId,
        productHandle: this.context?.productHandle,
        actProductSlug: this.context?.actProductSlug,
        counterMode: this.context?.counterMode,
        ...detail,
      };

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      document.dispatchEvent(new CustomEvent('tonys-counter:analytics', { detail: payload }));
    }

    createElement(tagName, className, text) {
      const element = document.createElement(tagName);
      if (className) element.className = className;
      if (text) element.textContent = text;
      return element;
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.tonysCounterInitialized === 'true') return;
      root.dataset.tonysCounterInitialized = 'true';
      new TonysCounter(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
